import { EBookingStatus } from '../enums/booking.enum';
import {
    BookingStopStatus,
    BookingStopType,
    IBooking,
    ICoordinates,
} from '../interfaces/booking.interface';

export interface DisplayStop {
    id: string;
    order: number;
    kind: BookingStopType;
    business?: string;
    address: string;
    references?: string;
    status: BookingStopStatus;
    lat: number;
    lng: number;
}

// Estados del booking en los que el primer pickup (sintetizado desde root) ya se recogió.
const PICKED_UP_STATUSES: EBookingStatus[] = [
    EBookingStatus.WAY_TO_DELIVER,
    EBookingStatus.ARRIVED_AT_DELIVERY,
    EBookingStatus.COMPLETED,
    EBookingStatus.RETURNING,
    EBookingStatus.RETURNED,
];

const toLatLng = (coords?: ICoordinates): { lat: number; lng: number } | null => {
    const c = coords?.coordinates;
    return c && c.length === 2 ? { lat: c[1], lng: c[0] } : null;
};

/**
 * Normaliza un booking MULTI_PICKING a una lista ordenada de paradas para la UI.
 * "Magia" de datos: la parada #1 se sintetiza desde los campos pickup* a nivel root
 * (el primer local). bookingStops aporta las paradas #2..N. Si bookingStops no incluye
 * un stop DELIVERY, la entrega final se arma desde los campos delivery* root.
 */
export const buildDisplayStops = (booking: IBooking): DisplayStop[] => {
    const result: DisplayStop[] = [];

    const rootPickup = toLatLng(booking.pickupCoordinates);
    if (rootPickup) {
        const pickedUp = PICKED_UP_STATUSES.includes(booking.statusText);
        result.push({
            id: 'root-pickup',
            order: 1,
            kind: 'PICKUP',
            business: booking.businessName,
            address: booking.pickupAddress,
            references: booking.pickupReferences ?? undefined,
            status: pickedUp ? 'PICKED_UP' : 'PENDING',
            ...rootPickup,
        });
    }

    const extraStops = [...(booking.bookingStops || [])].sort(
        (a, b) => a.orderIndex - b.orderIndex,
    );
    extraStops.forEach((stop, idx) => {
        const ll = toLatLng(stop.coordinates);
        if (!ll) return;
        result.push({
            id: stop._id || `stop-${idx}`,
            order: result.length + 1,
            kind: stop.type,
            business: stop.businessName,
            address: stop.address,
            references: stop.references,
            status: stop.status,
            ...ll,
        });
    });

    const hasDelivery = result.some((stop) => stop.kind === 'DELIVERY');
    const rootDelivery = toLatLng(booking.deliveryCoordinates);
    if (!hasDelivery && rootDelivery) {
        const completed = booking.statusText === EBookingStatus.COMPLETED;
        result.push({
            id: 'root-delivery',
            order: result.length + 1,
            kind: 'DELIVERY',
            business: booking.customerName,
            address: booking.deliveryAddress,
            references: booking.deliveryReferences,
            status: completed ? 'COMPLETED' : 'PENDING',
            ...rootDelivery,
        });
    }

    return result;
};

export const countPickups = (stops: DisplayStop[]): number => (
    stops.filter((s) => s.kind === 'PICKUP').length
);

export const countCompletedPickups = (stops: DisplayStop[]): number => (
    stops.filter((s) => s.kind === 'PICKUP' && (s.status === 'PICKED_UP' || s.status === 'COMPLETED')).length
);
